// src/components/common/PriceInput.tsx
import React, { forwardRef, useState, useRef, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';

interface PriceInputProps {
    value: Number | null | undefined;
    onChange: (value: number | null) => void;
    label?: string;
    placeholder?: string;
    helpText?: string;
    note?: string;
    required?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    hideOptional?: boolean;
    error?: string;
    name?: string;
    onBlur?: () => void;
    className?: string;
    isReviewMode?: boolean;
}

const PriceInput = forwardRef<HTMLInputElement, PriceInputProps>(({
    value,
    onChange,
    label,
    placeholder = '0.00',
    helpText = '',
    note,
    required = false,
    disabled = false,
    readOnly = false,
    hideOptional = false,
    error,
    name,
    onBlur,
    className = '',
    isReviewMode = false,
}, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [displayValue, setDisplayValue] = useState<string>('');
    const [isFocused, setIsFocused] = useState(false);

    // Sync display value when prop value changes
    useEffect(() => {
        if (value === null || value === undefined) {
            setDisplayValue('');
        } else {
            // ✅ Only format with commas, no forced .00
            if (isReviewMode)
                setDisplayValue(formatWithCommas(Math.round(value * 100) / 100));
            else
                setDisplayValue(formatWithCommasOnly(value));
        }
    }, [value]);

    // Format number with commas (preserves decimal places)
    const formatWithCommas = (num: number): string => {
        // ✅ Always use toFixed(2) to ensure 2 decimal places
        return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };
    const formatWithCommasOnly = (num: number): string => {
        const str = String(num);
        const parts = str.split('.');
        const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return parts.length > 1 ? `${integerPart}.${parts[1]}` : integerPart;
    };

    // ✅ Parse and clean the input value
    const cleanInput = (raw: string): string => {
        // Remove existing commas
        let clean = raw.replace(/,/g, '');

        // Allow only numbers and decimal point
        clean = clean.replace(/[^0-9.]/g, '');

        // Prevent multiple decimals
        const parts = clean.split('.');
        if (parts.length > 2) {
            clean = parts[0] + '.' + parts.slice(1).join('');
        }

        // Limit to 2 decimal places
        if (clean.includes('.')) {
            const decimalParts = clean.split('.');
            if (decimalParts[1] && decimalParts[1].length > 2) {
                clean = decimalParts[0] + '.' + decimalParts[1].substring(0, 2);
            }
        }

        return clean;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        const cursorPos = e.target.selectionStart || 0;

        // ✅ Clean the input (remove invalid characters, limit decimals)
        const cleaned = cleanInput(raw);

        // ✅ Update display (without formatting)
        setDisplayValue(cleaned);

        // ✅ Parse and emit value (removing commas is handled in cleanInput)
        const numValue = cleaned === '' || cleaned === '.' ? null : parseFloat(cleaned);
        onChange(isNaN(numValue as number) ? null : numValue);

        // ✅ Restore cursor position
        setTimeout(() => {
            if (inputRef.current) {
                const newPos = Math.min(cursorPos, inputRef.current.value.length);
                inputRef.current.setSelectionRange(newPos, newPos);
            }
        }, 0);
    };

    const handleBlur = () => {
        setIsFocused(false);

        // ✅ Format on blur: add commas and ensure 2 decimal places
        if (value !== null && value !== undefined && value >= 0) {
            // Round to 2 decimal places
            const rounded = Math.round(value * 100) / 100;
            onChange(rounded);
            // ✅ Format with commas and 2 decimal places
            setDisplayValue(formatWithCommas(rounded));
        } else if (displayValue === '' || displayValue === '.') {
            // Empty or just decimal point → clear
            onChange(null);
            setDisplayValue('');
        }

        onBlur?.();
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(true);
        // Select all text for easy editing
        e.target.select();
    };

    return (
        <div className={`space-y-1 ${className}`}>
            {label && (
                <label className="block text-sm font-medium text-gray-700">
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                    {!required && !hideOptional && <span className="text-xs text-gray-400 ml-1">(optional)</span>}
                    {helpText && <span className="text-xs text-gray-400 ml-1">({helpText})</span>}
                </label>
            )}

            <div className="relative">
                <span className="absolute left-3 top-2.5 text-gray-500">$</span>
                <input
                    ref={(node) => {
                        if (typeof ref === 'function') ref(node);
                        else if (ref) ref.current = node;
                        inputRef.current = node;
                    }}
                    type="text"
                    inputMode="decimal"
                    name={name}
                    value={displayValue}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    placeholder={placeholder}
                    disabled={disabled}
                    readOnly={readOnly}
                    className={`w-full pl-8 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${error ? 'border-red-500 bg-red-50' : 'border-gray-300'
                        } ${disabled || readOnly ? 'bg-gray-100 text-gray-500' : ''}`}
                />
            </div>

            {note && !error && (
                <p className="text-xs text-gray-400 italic">
                    {note}
                </p>
            )}

            {error && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {error}
                </p>
            )}
        </div>
    );
});

PriceInput.displayName = 'PriceInput';

export default PriceInput;